import { formatCurrency } from "@/components/Layout";
import { X, ArrowRight } from "lucide-react";

export const QuickPreviewCard = ({ 
  dayData, 
  date, 
  currency, 
  position, 
  onViewDetails, 
  onClose,
  isMobile,
  afterThisDay
}) => {
  const { income, bills, debt, totalIn, totalOut, balance } = dayData;
  
  const topIncome = income.slice(0, 3);
  const topBills = bills.slice(0, 3);
  const topDebt = debt.slice(0, 3);
  const hasTransactions = dayData.events.length > 0;
  
  // Find biggest expense for explanation
  const allExpenses = [...bills, ...debt].sort((a, b) => b.amount - a.amount);
  const biggestExpense = allExpenses[0];
  
  // Simple status
  const getStatus = () => {
    if (!hasTransactions) return null;
    if (balance >= 0) return { type: 'ok', message: `${formatCurrency(balance, currency)} left over` };
    return { 
      type: 'short', 
      message: biggestExpense 
        ? `Short ${formatCurrency(Math.abs(balance), currency)} because of ${biggestExpense.name}`
        : `Short ${formatCurrency(Math.abs(balance), currency)}`
    };
  };
  
  const status = getStatus();
  
  const formattedDate = date.toLocaleDateString('en-GB', { 
    weekday: 'long',
    day: 'numeric', 
    month: 'long'
  });

  const cardBg = "bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900";

  // Mobile bottom sheet
  if (isMobile) {
    return (
      <div className="fixed inset-x-0 bottom-0 z-50 animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className={`${cardBg} rounded-t-3xl shadow-2xl max-h-[75vh] overflow-hidden`}>
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-10 h-1.5 bg-white/30 rounded-full" />
          </div>
          
          <div className="px-5 pb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">{formattedDate}</h3>
              <button onClick={onClose} className="p-1.5 rounded-full bg-white/10 text-white/60">
                <X className="w-4 h-4" />
              </button>
            </div>
            
            {hasTransactions ? (
              <>
                {/* Summary Row */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="text-center p-2 rounded-xl bg-white/5">
                    <p className="text-[10px] text-white/50 uppercase">Income</p>
                    <p className="font-mono text-sm font-semibold text-emerald-400">{formatCurrency(totalIn, currency)}</p>
                  </div>
                  <div className="text-center p-2 rounded-xl bg-white/5">
                    <p className="text-[10px] text-white/50 uppercase">Outgoing</p>
                    <p className="font-mono text-sm font-semibold text-red-400">{formatCurrency(totalOut, currency)}</p>
                  </div>
                  <div className="text-center p-2 rounded-xl bg-white/5">
                    <p className="text-[10px] text-white/50 uppercase">Left Over</p>
                    <p className={`font-mono text-sm font-semibold ${balance >= 0 ? 'text-white' : 'text-red-400'}`}>
                      {balance < 0 && '-'}{formatCurrency(Math.abs(balance), currency)}
                    </p>
                  </div>
                </div>
                
                {/* AFTER THIS DAY - Bold */}
                {afterThisDay !== undefined && (
                  <div className={`flex items-center justify-between p-4 rounded-xl mb-4 ${
                    afterThisDay < 0 ? 'bg-red-500/20 border border-red-500/30' :
                    afterThisDay <= 100 ? 'bg-amber-500/15 border border-amber-500/30' :
                    'bg-emerald-500/10 border border-emerald-500/20'
                  }`}>
                    <span className="text-sm font-bold text-white">After this day:</span>
                    <span className={`font-mono text-xl font-bold ${
                      afterThisDay < 0 ? 'text-red-400' : afterThisDay <= 100 ? 'text-amber-400' : 'text-emerald-400'
                    }`}>
                      {afterThisDay < 0 && '-'}{formatCurrency(Math.abs(afterThisDay), currency)}
                    </span>
                  </div>
                )}
                
                {/* Transaction Lists */}
                <div className="space-y-3 mb-5 max-h-40 overflow-y-auto">
                  {topIncome.length > 0 && (
                    <div>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1.5">Income</p>
                      {topIncome.map((item, i) => (
                        <div key={i} className="flex justify-between py-1">
                          <span className="text-sm text-white/80">{item.name}</span>
                          <span className="font-mono text-sm text-emerald-400">{formatCurrency(item.amount, currency)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {topBills.length > 0 && (
                    <div>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1.5">Bills</p>
                      {topBills.map((item, i) => (
                        <div key={i} className="flex justify-between py-1">
                          <span className="text-sm text-white/80">{item.name}</span>
                          <span className="font-mono text-sm text-red-400">{formatCurrency(item.amount, currency)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {topDebt.length > 0 && (
                    <div>
                      <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1.5">Debt Payments</p>
                      {topDebt.map((item, i) => (
                        <div key={i} className="flex justify-between py-1">
                          <span className="text-sm text-white/80">{item.name}</span>
                          <span className="font-mono text-sm text-purple-400">{formatCurrency(item.amount, currency)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <button 
                  onClick={onViewDetails}
                  className="w-full py-3.5 bg-white text-slate-900 font-semibold rounded-xl flex items-center justify-center gap-2"
                  data-testid="preview-view-details-btn"
                >
                  View Details <ArrowRight className="w-4 h-4" />
                </button>
              </>
            ) : (
              <div className="text-center py-8">
                <p className="text-white/50 mb-4">No transactions</p>
                <button onClick={onViewDetails} className="text-emerald-400 text-sm font-medium">
                  + Add transaction
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop floating tooltip
  return (
    <div 
      className="fixed z-50 animate-fade-in"
      style={{ left: position.x, top: position.y }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={`${cardBg} rounded-2xl shadow-2xl w-80 overflow-hidden border border-white/10`}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-white">{formattedDate}</h3>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-white/10 text-white/40">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="p-4">
          {hasTransactions ? (
            <>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="text-center">
                  <p className="text-[10px] text-white/40">Income</p>
                  <p className="font-mono text-sm font-semibold text-emerald-400">{formatCurrency(totalIn, currency)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-white/40">Outgoing</p>
                  <p className="font-mono text-sm font-semibold text-red-400">{formatCurrency(totalOut, currency)}</p>
                </div>
                <div className="text-center">
                  <p className="text-[10px] text-white/40">Left Over</p>
                  <p className={`font-mono text-sm font-semibold ${balance >= 0 ? 'text-white' : 'text-red-400'}`}>
                    {balance < 0 && '-'}{formatCurrency(Math.abs(balance), currency)}
                  </p>
                </div>
              </div>
              
              {/* AFTER THIS DAY */}
              {afterThisDay !== undefined && (
                <div className={`flex items-center justify-between p-3 rounded-xl mb-3 ${
                  afterThisDay < 0 ? 'bg-red-500/20 border border-red-500/30' :
                  afterThisDay <= 100 ? 'bg-amber-500/15 border border-amber-500/30' :
                  'bg-emerald-500/10 border border-emerald-500/20'
                }`}>
                  <span className="text-xs font-bold text-white">After this day:</span>
                  <span className={`font-mono text-base font-bold ${
                    afterThisDay < 0 ? 'text-red-400' : afterThisDay <= 100 ? 'text-amber-400' : 'text-emerald-400'
                  }`}>
                    {afterThisDay < 0 && '-'}{formatCurrency(Math.abs(afterThisDay), currency)}
                  </span>
                </div>
              )}
              
              {/* Transactions */}
              <div className="space-y-2 mb-4 max-h-32 overflow-y-auto">
                {topIncome.map((item, i) => (
                  <div key={`inc-${i}`} className="flex justify-between text-xs">
                    <span className="text-white/70">{item.name}</span>
                    <span className="font-mono text-emerald-400">{formatCurrency(item.amount, currency)}</span>
                  </div>
                ))}
                {topBills.map((item, i) => (
                  <div key={`bill-${i}`} className="flex justify-between text-xs">
                    <span className="text-white/70">{item.name}</span>
                    <span className="font-mono text-red-400">{formatCurrency(item.amount, currency)}</span>
                  </div>
                ))}
                {topDebt.map((item, i) => (
                  <div key={`debt-${i}`} className="flex justify-between text-xs">
                    <span className="text-white/70">{item.name}</span>
                    <span className="font-mono text-purple-400">{formatCurrency(item.amount, currency)}</span>
                  </div>
                ))}
              </div>
              
              <button 
                onClick={onViewDetails}
                className="w-full py-2.5 bg-white text-slate-900 text-sm font-semibold rounded-xl flex items-center justify-center gap-1.5"
                data-testid="preview-view-details-btn"
              >
                View Details <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <div className="text-center py-6">
              <p className="text-white/40 text-sm mb-3">No transactions</p>
              <button onClick={onViewDetails} className="text-emerald-400 text-sm font-medium">
                + Add transaction
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickPreviewCard;
